/**
 * SelectedMatchInfoView - Displays rating, opponent, and date for a selected bar
 *
 * Renders an inline info row above the chart for all match statuses including DNP.
 * Always reserves its height so the chart does not shift when a bar is hovered or tapped.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import type { MatchStatus } from "@/features/player-detail/types";

/**
 * Performance data subset needed for the info display
 */
interface SelectedPerformance {
  readonly opponent: string;
  readonly status: MatchStatus;
  readonly rating: number;
  readonly formattedDate: string;
}

/**
 * Props for SelectedMatchInfoView
 */
interface SelectedMatchInfoViewProps {
  /** The selected performance, or null when nothing is hovered */
  readonly selectedPerformance: SelectedPerformance | null;
  /** Whether dark mode is active */
  readonly isDark: boolean;
}

/**
 * SelectedMatchInfoView renders an info row for the hovered/tapped bar
 * @param props - View props
 * @param props.selectedPerformance - The selected performance data
 * @param props.isDark - Whether dark mode is active
 * @returns HStack with match info or invisible placeholder to reserve height
 */
const SelectedMatchInfoView = ({
  selectedPerformance,
}: SelectedMatchInfoViewProps) => (
  <Stack
    direction="horizontal"
    {...{
      testID: TEST_ID_PLAYER_DETAIL.SELECTED_MATCH_INFO,
      "data-testid": TEST_ID_PLAYER_DETAIL.SELECTED_MATCH_INFO,
    }}
    className={`items-center justify-center gap-2 rounded-none px-3 py-1 ${
      selectedPerformance ? "bg-surface-raised" : ""
    }`}
  >
    {selectedPerformance ? (
      <>
        <Text variant="title-sm" className="text-accent-primary">
          {selectedPerformance.status === "dnp"
            ? "DNP"
            : selectedPerformance.rating.toFixed(1)}
        </Text>
        <Text variant="caption" className="text-content-secondary">
          vs {selectedPerformance.opponent}
        </Text>
        <Text variant="caption" className="text-content-muted">
          {selectedPerformance.formattedDate}
        </Text>
      </>
    ) : (
      <Text variant="unstyled" className="text-title-sm opacity-0">
        —
      </Text>
    )}
  </Stack>
);

SelectedMatchInfoView.displayName = "SelectedMatchInfoView";

export default memo(SelectedMatchInfoView);
